import { useRef, useState } from "react";
import { Image as ImageIcon, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatErr } from "@/lib/api";
import { HEADING_FONTS, BODY_FONTS } from "@/components/LandingPage";

const chip = (on) => `h-9 px-3 rounded-full text-xs border transition-colors ${on ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-[#242C3D] text-slate-400 hover:text-white"}`;

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-1.5">
    <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500 pt-2 shrink-0 w-28">{label}</span>
    <div className="flex-1 flex flex-wrap gap-2 justify-end items-center">{children}</div>
  </div>
);

const ColorInput = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <input type="color" data-testid={`${testid}-picker`} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 rounded-md bg-transparent border border-[#242C3D] cursor-pointer p-1" />
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-24 px-2 rounded-md bg-[#0B0F17] border border-[#242C3D] text-xs font-mono focus:outline-none focus:border-indigo-500" />
  </div>
);

export const ImageUpload = ({ value, onChange, testid, emptyLabel }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/images/upload", fd);
      onChange(data.path);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`${testid}-input`} onChange={(e) => upload(e.target.files[0])} />
      {value ? (
        <>
          <img src={assetUrl(value)} alt="" data-testid={`${testid}-preview`} className="h-10 max-w-[120px] rounded-md border border-[#242C3D] object-contain bg-black/30" />
          <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange("")} className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-rose-400 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
        </>
      ) : (
        <button type="button" data-testid={testid} disabled={busy} onClick={() => ref.current?.click()} className={chip(false)}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : <ImageIcon className="w-3.5 h-3.5 inline mr-1" />}
          {emptyLabel}
        </button>
      )}
    </div>
  );
};

const FontSelect = ({ value, onChange, options, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
    className="h-9 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-xs focus:outline-none focus:border-indigo-500">
    {options.map((f) => <option key={f} value={f}>{f}</option>)}
  </select>
);

const SIZE_FIELDS = [["title", "Heading", 64], ["header", "Header line", 30], ["description", "Description", 16], ["form_heading", "Form heading", 20], ["form_label", "Form labels", 11]];

const FontSizes = ({ sizes, setSizes }) => (
  <Row label="Font sizes">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full" data-testid="branding-font-sizes">
      {SIZE_FIELDS.map(([k, label, def]) => (
        <label key={k} className="text-[11px] text-slate-400 space-y-1">
          <span className="block">{label}</span>
          <div className="flex items-center gap-1">
            <input type="number" min={0} max={120} data-testid={`branding-font-size-${k}`} value={sizes[k] || ""} placeholder={`${def}`}
              onChange={(e) => setSizes({ ...sizes, [k]: Math.max(0, Math.min(120, parseInt(e.target.value, 10) || 0)) })}
              className="h-8 w-full px-2 rounded-md bg-[#0B0F17] border border-[#242C3D] text-xs font-mono focus:outline-none focus:border-indigo-500" />
            <span className="text-slate-500">px</span>
          </div>
        </label>
      ))}
      <p className="col-span-full text-[11px] text-slate-500">Leave empty for the default size (min 10px).</p>
    </div>
  </Row>
);

export const DesignPanel = ({ branding: b, setBranding }) => {
  const set = (patch) => setBranding({ ...b, ...patch });
  const opts = (val, key, list, prefix) => list.map(([v, label]) => (
    <button key={v} type="button" data-testid={`branding-${prefix}-${v}`} onClick={() => set({ [key]: v })} className={chip(val === v)}>{label}</button>
  ));
  return (
    <div className="space-y-1" data-testid="design-panel">
      <Row label="Logo"><ImageUpload value={b.logo_path} onChange={(p) => set({ logo_path: p })} testid="branding-logo-upload" emptyLabel="Upload logo" /></Row>
      {b.logo_path && (
        <>
          <Row label="Logo position">{opts(b.logo_position, "logo_position", [["left", "Left"], ["center", "Center"], ["right", "Right"]], "logo-pos")}</Row>
          <Row label="Logo size">{opts(b.logo_size, "logo_size", [["sm", "Small"], ["md", "Medium"], ["lg", "Large"]], "logo-size")}</Row>
        </>
      )}
      <Row label="Theme">{opts(b.theme, "theme", [["dark", "Dark"], ["light", "Light"]], "theme")}</Row>
      <Row label="Accent"><ColorInput value={b.accent} onChange={(v) => set({ accent: v })} testid="branding-accent-input" /></Row>
      <Row label="Background">{opts(b.bg_type, "bg_type", [["color", "Colour"], ["image", "Image"]], "bg-type")}</Row>
      {b.bg_type === "color" ? (
        <Row label="BG colour"><ColorInput value={b.bg_color} onChange={(v) => set({ bg_color: v })} testid="branding-bg-color-input" /></Row>
      ) : (
        <Row label="BG image"><ImageUpload value={b.bg_image_path} onChange={(p) => set({ bg_image_path: p })} testid="branding-bg-image-upload" emptyLabel="Upload background" /></Row>
      )}
      <Row label="Layout">{opts(b.layout, "layout", [["form-left", "Form left"], ["form-right", "Form right"], ["center", "Centred"]], "layout")}</Row>
      <Row label="Button">{opts(b.button_style, "button_style", [["pill", "Pill"], ["sharp", "Sharp"]], "button-style")}</Row>
      <Row label="Heading colour">
        <ColorInput value={b.heading_color || (b.theme === "light" ? "#0F172A" : "#FFFFFF")} onChange={(v) => set({ heading_color: v })} testid="branding-heading-color-input" />
        {b.heading_color && <button type="button" data-testid="branding-heading-color-reset" onClick={() => set({ heading_color: "" })} className="text-xs text-slate-400 hover:text-white underline">Default</button>}
      </Row>
      <Row label="Form display">{opts(b.form_mode, "form_mode", [["inline", "Show form"], ["popup", "CTA button → pop-up form"]], "form-mode")}</Row>
      <FontSizes sizes={b.font_sizes} setSizes={(font_sizes) => set({ font_sizes })} />
      <Row label="Heading font"><FontSelect value={b.font_heading} onChange={(v) => set({ font_heading: v })} options={HEADING_FONTS} testid="branding-font-heading-select" /></Row>
      <Row label="Body font"><FontSelect value={b.font_body} onChange={(v) => set({ font_body: v })} options={BODY_FONTS} testid="branding-font-body-select" /></Row>
    </div>
  );
};
